import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';
import { formatApiError } from '@/lib/validators';
import { addressCovered, suggestedCidr } from '@/lib/network';
import { formatIST } from '@/lib/utils';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, RefreshCw, MapPin, AlertTriangle } from 'lucide-react';

import { Screen, Pane, CommandBar, DataGrid, CellButton } from '@/components/data';
import PhoneNetworkAccess from './Admin/PhoneNetworkAccess';
import { useIsMobile } from '@/hooks/useIsMobile';

// what each mode actually does, in the words the person switching it needs
const MODES = [
  { value: 'off', label: 'Off', blurb: 'The allowlist is ignored. Nothing is recorded.' },
  { value: 'monitor', label: 'Monitor', blurb: 'Nobody is refused. Every request that would have been refused is recorded below.' },
  { value: 'enforce', label: 'Enforce', blurb: 'Accounts outside the allowed networks are refused, admins excepted.' },
];

// Cloudflare fronts both hostnames. Seeing one of its addresses here means the
// edge is not passing the real client through, and enforcing would refuse the
// whole firm — worth saying loudly on the screen rather than in a runbook.
const CLOUDFLARE_HINT = /^(104\.(1[6-9]|2[0-9]|3[01])\.|172\.6[4-9]\.|172\.7[0-1]\.|2606:4700)/;

export default function NetworkAccessPage() {
  const isMobile = useIsMobile();
  const [policy, setPolicy] = useState(null);
  const [refusals, setRefusals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({ label: '', cidr: '' });
  const [removing, setRemoving] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get('/admin/network-policy'),
        api.get('/admin/network-policy/refusals'),
      ]);
      setPolicy(p.data);
      setRefusals(r.data.refusals || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load network access settings'));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setMode = async (mode) => {
    setSaving(true);
    try {
      await api.put('/admin/network-policy', { mode, exempt_roles: policy.exempt_roles });
      setPolicy((p) => ({ ...p, mode }));
      toast.success(`Network access set to ${mode}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not change the mode'));
    }
    setSaving(false);
  };

  const addEntry = async (entry) => {
    setSaving(true);
    try {
      await api.post('/admin/network-policy/entries', entry);
      toast.success(`${entry.cidr} added`);
      setAddOpen(false);
      setNewEntry({ label: '', cidr: '' });
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not add that network'));
    }
    setSaving(false);
  };

  const removeEntry = async () => {
    const entry = removing;
    setRemoving(null);
    try {
      await api.delete(`/admin/network-policy/entries/${entry.id}`);
      toast.success(`${entry.cidr} removed`);
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e, 'Could not remove that network'));
    }
  };

  const address = policy?.your_address || '';
  const edgeLooksWrong = CLOUDFLARE_HINT.test(address);
  const suggested = suggestedCidr(address);
  const alreadyListed = addressCovered(address, policy?.entries);

  const entryColumns = [
    {
      key: 'label',
      header: 'Location',
      width: 'minmax(10rem,1fr)',
      render: (e) => <span className="block truncate font-semibold text-slate-900">{e.label || '—'}</span>,
    },
    {
      key: 'cidr',
      header: 'Network',
      width: 'minmax(14rem,1fr)',
      render: (e) => <span className="block truncate font-mono text-[11px] text-slate-700">{e.cidr}</span>,
    },
    {
      key: 'added_by', header: 'Added by', width: 'minmax(8rem,0.7fr)', mobile: false,
      render: (e) => <span className="block truncate text-slate-500">{e.added_by || '—'}</span>,
    },
    {
      key: 'added_at', header: 'Added', width: '7rem', mobile: false,
      render: (e) => <span className="text-slate-500">{e.added_at ? e.added_at.slice(0, 10) : '—'}</span>,
    },
    {
      key: 'remove', header: '', width: '4rem', align: 'right',
      render: (e) => (
        <CellButton icon={Trash2} tone="danger" iconOnly label={`Remove ${e.cidr}`}
          onClick={() => setRemoving(e)} />
      ),
    },
  ];

  const refusalColumns = [
    {
      key: 'user_name', header: 'Account', width: 'minmax(10rem,1fr)',
      render: (r) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{r.user_name || r.user_id}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{(r.roles || []).join(', ')}</span>
        </>
      ),
    },
    {
      key: 'address', header: 'Seen from', width: 'minmax(13rem,1fr)',
      render: (r) => <span className="block truncate font-mono text-[11px] text-slate-700">{r.address}</span>,
    },
    { key: 'count', header: 'Requests', width: '6rem', align: 'right', render: (r) => r.count },
    {
      // the count is for the day, so the day alone leaves "is he still being
      // refused right now?" unanswered — the last one carries its time
      key: 'last_seen', header: 'Last seen', width: '9.5rem',
      render: (r) => (
        <span className="whitespace-nowrap text-slate-600">
          {formatIST(r.last_seen, { compact: true })}
        </span>
      ),
    },
    {
      key: 'last_path', header: 'Last call', width: 'minmax(10rem,1fr)', mobile: false,
      render: (r) => <span className="block truncate font-mono text-[11px] text-slate-500">{r.last_path}</span>,
    },
  ];

  const mode = policy?.mode || 'off';
  const modeBlurb = MODES.find((m) => m.value === mode)?.blurb;

  const panels = (
    <>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add an allowed network</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="net-label">Location</Label>
              <Input id="net-label" placeholder="Main office"
                value={newEntry.label}
                onChange={(e) => setNewEntry((n) => ({ ...n, label: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="net-cidr">Address or range</Label>
              <Input id="net-cidr" placeholder="223.185.53.105  or  2401:4900:8832:732b::/64"
                className="font-mono"
                value={newEntry.cidr}
                onChange={(e) => setNewEntry((n) => ({ ...n, cidr: e.target.value }))} />
              <p className="mt-1 text-[11px] leading-tight text-slate-500">
                An IPv6 address must be added as a <span className="font-mono">/64</span> range —
                the last four groups change every few hours on the same device, so a full address
                stops matching by itself.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button disabled={saving || !newEntry.cidr.trim()} onClick={() => addEntry(newEntry)}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.cidr}?</AlertDialogTitle>
            <AlertDialogDescription>
              While enforcing, anyone on {removing?.label || 'this network'} will be signed out of
              the software on their next request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeEntry}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (isMobile) {
    return (
      <>
        <PhoneNetworkAccess
          policy={policy}
          refusals={refusals}
          loading={loading}
          saving={saving}
          mode={mode}
          modes={MODES}
          modeBlurb={modeBlurb}
          address={address}
          edgeLooksWrong={edgeLooksWrong}
          suggested={suggested}
          alreadyListed={alreadyListed}
          onSetMode={setMode}
          onAddSuggested={() => addEntry({ label: 'Added from this device', cidr: suggested })}
          onAdd={() => setAddOpen(true)}
          onRefresh={fetchAll}
          onRemove={setRemoving}
        />
        {panels}
      </>
    );
  }

  return (
    <Screen>
      <CommandBar
        title="Network Access"
        subtitle="Which networks the software may be used from"
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchAll },
          { key: 'add', label: 'Add Network', icon: Plus, tone: 'accent', onClick: () => setAddOpen(true) },
        ]}
      />

      {/* Both of these are one line of text each. Stacked they cost two title
          bars of the viewport that the two lists below need more. */}
      <div className="grid shrink-0 gap-2.5 lg:grid-cols-2">
        <Pane title="This device" tone="plain" scroll={false}>
          <div className="flex flex-wrap items-center gap-3 p-3">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="text-sm text-slate-600">
              The server sees this device as{' '}
              <span className="font-mono font-semibold text-slate-900">{address || 'unknown'}</span>
            </span>
            {suggested && !alreadyListed && (
              <Button size="sm" variant="outline"
                onClick={() => addEntry({ label: 'Added from this device', cidr: suggested })}>
                Add {suggested}
              </Button>
            )}
            {alreadyListed && <span className="text-xs text-emerald-600">already on the list</span>}
          </div>

          {edgeLooksWrong && (
            <div className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                That is a Cloudflare address, not this device's. The edge is not passing the real
                client through, so every account would be refused. Fix the nginx <code>real_ip</code>
                {' '}configuration in <code>docs/DEPLOYMENT.md</code> before switching to Enforce.
              </span>
            </div>
          )}
        </Pane>

        <Pane title="Mode" tone="plain" scroll={false}>
          <div className="flex flex-wrap items-center gap-3 p-3">
            <Select value={mode} onValueChange={setMode} disabled={saving || loading}>
              <SelectTrigger className="w-40" aria-label="Network access mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">{modeBlurb}</span>
          </div>
        </Pane>
      </div>

      {/* The allowlist is a handful of rows on a normal day, so it takes only
          what it needs and hands the rest of the screen to the refusal log. */}
      <Pane
        title="Allowed networks"
        aside={`${policy?.entries?.length || 0} entr${policy?.entries?.length === 1 ? 'y' : 'ies'}`}
        className="shrink-0 lg:max-h-[38vh]"
      >
        <DataGrid
          data-testid="allowlist-table"
          columns={entryColumns}
          rows={policy?.entries || []}
          getRowId={(e) => e.id}
          loading={loading}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Shield className="h-8 w-8 text-slate-300" />
              No networks yet — nobody is refused until at least one is added
            </span>
          }
        />
      </Pane>

      <Pane
        title={mode === 'enforce' ? 'Refused' : 'Would be refused'}
        aside={mode === 'monitor' ? 'recorded, not blocked' : undefined}
        className="min-h-0 flex-1"
      >
        <DataGrid
          data-testid="refusals-table"
          columns={refusalColumns}
          rows={refusals}
          getRowId={(r) => r.id}
          loading={loading}
          empty={<span className="text-slate-400">Nothing recorded</span>}
        />
      </Pane>

      {panels}
    </Screen>
  );
}
